import api from "lib/axios";
import type { ApiEnvelope } from "services/apiTypes";
import { normalizeChatMessage } from "utils/normalizeChatMessagesResponse";
import type { CourseChatMessage } from "services/courseChatService";

export type DirectChatPeer = {
  id: number;
  name: string;
  role: string;
  avatar_url: string | null;
};

export type DirectChatInboxItem = {
  conversation_id: number | null;
  peer: DirectChatPeer;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_type: string | null;
};

export type DirectChatBootstrapResponse = {
  conversation: {
    id: number;
    instructor_id: number;
    student_id: number;
  };
  peer: DirectChatPeer;
  firebase: {
    uid: string;
    conversation_path: string;
    messages_path: string;
  };
  permissions: {
    can_read: boolean;
    can_send: boolean;
  };
  notifications_muted?: boolean;
};

function assertApiEnvelope(data: unknown): asserts data is ApiEnvelope<unknown> {
  if (data == null || typeof data !== "object" || !("status" in data)) {
    throw new Error("Invalid API response — check REACT_APP_API_URL points to the Laravel API.");
  }
}

function unwrapResult<T>(envelope: ApiEnvelope<unknown>): T {
  const result = envelope.result;
  if (result && typeof result === "object" && "data" in (result as object)) {
    return (result as { data: T }).data;
  }
  return result as T;
}

function toUiMessage(raw: CourseChatMessage): CourseChatMessage {
  const anyRaw = raw as CourseChatMessage & { conversation_id?: number };
  return {
    ...raw,
    course_id: Number(anyRaw.conversation_id ?? raw.course_id ?? 0),
  };
}

export const directChatService = {
  async listConversations(): Promise<DirectChatInboxItem[]> {
    const res = await api.get("/instructors/direct-chat/conversations", {
      meta: { toastSuccess: false, toastError: false },
    });
    assertApiEnvelope(res.data);
    const payload = unwrapResult<{ conversations: DirectChatInboxItem[] }>(res.data);
    return payload.conversations ?? [];
  },

  async openWithStudent(studentId: number | string): Promise<DirectChatBootstrapResponse> {
    const res = await api.post(
      `/instructors/direct-chat/with-student/${studentId}`,
      {},
      { meta: { toastSuccess: false, toastError: false } }
    );
    assertApiEnvelope(res.data);
    return unwrapResult<DirectChatBootstrapResponse>(res.data);
  },

  async bootstrap(conversationId: number | string): Promise<DirectChatBootstrapResponse> {
    const res = await api.get(`/instructors/direct-chat/conversations/${conversationId}/bootstrap`, {
      meta: { toastSuccess: false, toastError: false },
    });
    assertApiEnvelope(res.data);
    return unwrapResult<DirectChatBootstrapResponse>(res.data);
  },

  async setNotificationsMuted(
    conversationId: number | string,
    muted: boolean
  ): Promise<{ notifications_muted: boolean; conversation_id: number }> {
    const res = await api.put(
      `/instructors/direct-chat/conversations/${conversationId}/notification-mute`,
      { muted },
      { meta: { toastSuccess: false, toastError: false } }
    );
    assertApiEnvelope(res.data);
    return unwrapResult<{ notifications_muted: boolean; conversation_id: number }>(res.data);
  },

  async sendText(
    conversationId: number | string,
    body: string,
    clientMessageId?: string
  ): Promise<CourseChatMessage> {
    const res = await api.post(
      `/instructors/direct-chat/conversations/${conversationId}/messages`,
      { message_type: "text", body, client_message_id: clientMessageId },
      { meta: { toastSuccess: false, toastError: false } }
    );
    assertApiEnvelope(res.data);
    const message = normalizeChatMessage(res.data);
    if (!message) throw new Error("Invalid chat message response");
    return toUiMessage(message);
  },

  async sendAttachment(
    conversationId: number | string,
    messageType: "image" | "file" | "voice",
    file: File,
    options?: {
      body?: string;
      voice_duration_seconds?: number;
      clientMessageId?: string;
      onUploadProgress?: (percent: number) => void;
    }
  ): Promise<CourseChatMessage> {
    const form = new FormData();
    form.append("message_type", messageType);
    form.append("file", file);
    if (options?.body) form.append("body", options.body);
    if (options?.voice_duration_seconds != null) {
      form.append("voice_duration_seconds", String(options.voice_duration_seconds));
    }
    if (options?.clientMessageId) form.append("client_message_id", options.clientMessageId);

    const res = await api.post(
      `/instructors/direct-chat/conversations/${conversationId}/messages`,
      form,
      {
        meta: { toastSuccess: false, toastError: false },
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: options?.onUploadProgress
          ? (e) => {
              if (!e.total) return;
              options.onUploadProgress?.(Math.min(100, Math.round((e.loaded / e.total) * 100)));
            }
          : undefined,
      }
    );
    assertApiEnvelope(res.data);
    const message = normalizeChatMessage(res.data);
    if (!message) throw new Error("Invalid chat message response");
    return toUiMessage(message);
  },

  async resolveAttachmentUrl(conversationId: number | string, messageId: string): Promise<string> {
    const res = await api.get(
      `/instructors/direct-chat/conversations/${conversationId}/messages/${messageId}/attachment-url`,
      { meta: { toastSuccess: false, toastError: false } }
    );
    assertApiEnvelope(res.data);
    const payload = unwrapResult<{ url: string }>(res.data);
    if (!payload?.url) throw new Error("Missing attachment URL");
    return payload.url;
  },

  async deleteMessage(conversationId: number | string, messageId: string): Promise<void> {
    await api.delete(
      `/instructors/direct-chat/conversations/${conversationId}/messages/${messageId}`,
      { meta: { toastSuccess: false, toastError: false } }
    );
  },

  async heartbeat(conversationId: number | string, isActive = true): Promise<void> {
    await api.post(
      "/common/direct-chat/heartbeat",
      { conversation_id: Number(conversationId), is_active: isActive },
      { meta: { toastSuccess: false, toastError: false } }
    );
  },

  async leaveChat(conversationId: number | string): Promise<void> {
    await api.post(
      "/common/direct-chat/inactive",
      { conversation_id: Number(conversationId) },
      { meta: { toastSuccess: false, toastError: false } }
    );
  },
};
